import { ArrowLeft, Plus, MessageCircle, Mail, Trash2, FileDown, BookOpen, Loader2, RotateCcw, FileSearch, FileText, X } from "lucide-react";
import { useEffect, useState, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { DocumentoUploadModal } from "../DocumentoUploadModal";
import { PruebaSelector } from "../PruebaSelector";
import type { Pedido } from "../../types/pedido";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useFinalizarPedido } from "../../hooks/useFinalizarPedido";
import { useDescargarReportes } from "../../hooks/useDescargarReportes";
import { useVistaPrevia } from "../../hooks/useVistaPrevia";
import { FinalizarPedidoModal } from "./FinalizarPedidoModal";
import { ReabrirPedidoModal } from "./ReabrirPedidoModal";
import { PedidoPruebaItem } from "./PedidoPruebaItem";
import { actualizarOrdenPruebas, obtenerReaperturaOrigen, obtenerNotaDocCliente, actualizarNotaDocCliente, actualizarFechasPedido } from "../../services/pruebasService";
import { convertirWordANotaHtml } from "../../lib/wordToHtml";
import { Input } from "../ui/input";

function formatFechaLocal(value?: string): string {
  if (!value) return '—';
  const [y, m, d] = value.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX');
}

function formatHoraLocal(value?: string): string {
  const hora = value?.split('T')[1]?.slice(0, 5);
  return hora ? ` ${hora}` : '';
}

interface PruebaData {
  id: string;
  nombre: string;
  tipo: "Fisico" | "Quimico";
  descripcion: string;
  tiempoEstimado: string;
}

interface PedidoDetailProps {
  pedido: Pedido;
  onBack: () => void;
  onAddPrueba: (pedidoId: string, prueba: string) => void;
  onLlenarPrueba: (pedido: Pedido, prueba: string, pruebaIndex: number) => void;
  pruebasGuardadas: { [pedidoId: string]: { [prueba: string]: { row_id: string } } };
  handleRevisarPrueba: (pedido: Pedido, prueba: string, rowId?: string) => void;
  handleEditarPrueba?: (pedido: Pedido, prueba: string, rowId?: string) => void;
  handleContinuarBorrador?: (pedido: Pedido, prueba: string, rowId: string) => void;
  onToggleReporteCliente?: (nomId: string, incluir: boolean) => void;
  setPedidosState: Dispatch<SetStateAction<Pedido[]>>;
  onRefreshBackend?: () => void;
  showDocumentoUploadModal: boolean;
  setShowDocumentoUploadModal: (v: boolean) => void;
  documentoUploadInfo: { rowId: string; prueba: string; nomId?: string; pedidoId: string; origenNumero: string } | null;
  setDocumentoUploadInfo: (v: { rowId: string; prueba: string; nomId?: string; pedidoId: string; origenNumero: string } | null) => void;
  modoEdicionAdmin?: boolean;
  onExpirarModoAdmin?: () => void;
  puedeCapturar?: boolean;
  puedeCerrarPedido?: boolean;
  puedeVerBitacoras?: boolean;
  puedeReabrir?: boolean;
  userName?: string;
}

export function PedidoDetail({
  pedido,
  onBack,
  onAddPrueba,
  onLlenarPrueba,
  handleRevisarPrueba,
  handleEditarPrueba,
  handleContinuarBorrador,
  onToggleReporteCliente,
  setPedidosState,
  onRefreshBackend,
  showDocumentoUploadModal,
  setShowDocumentoUploadModal,
  documentoUploadInfo,
  setDocumentoUploadInfo,
  modoEdicionAdmin = false,
  onExpirarModoAdmin,
  puedeCapturar = true,
  puedeCerrarPedido = true,
  puedeVerBitacoras = true,
  puedeReabrir = false,
  userName = "",
}: PedidoDetailProps) {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [showReabrirModal, setShowReabrirModal] = useState(false);
  const [reaperturaOrigen, setReaperturaOrigen] = useState<{
    evidenciaUrl: string | null;
    solicitadoPor: string;
    createdAt: string;
    origenNumeroAnterior: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelado = false;
    obtenerReaperturaOrigen(pedido.id)
      .then(r => { if (!cancelado) setReaperturaOrigen(r); })
      .catch(err => console.error('No se pudo cargar el origen de reapertura:', err));
    return () => { cancelado = true; };
  }, [pedido.id]);

  const [notaDocCliente, setNotaDocCliente] = useState<string | null>(null);
  const [subiendoNotaDoc, setSubiendoNotaDoc] = useState(false);

  useEffect(() => {
    let cancelado = false;
    obtenerNotaDocCliente(pedido.id)
      .then(nota => { if (!cancelado) setNotaDocCliente(nota); })
      .catch(err => console.error('No se pudo cargar el documento Word del reporte cliente:', err));
    return () => { cancelado = true; };
  }, [pedido.id]);

  const handleNotaDocClienteChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendoNotaDoc(true);
    try {
      const html = await convertirWordANotaHtml(file);
      await actualizarNotaDocCliente(pedido.id, html);
      setNotaDocCliente(html);
      toast.success('Documento cargado correctamente');
    } catch (err: any) {
      toast.error('Error al procesar el archivo Word: ' + (err.message || err));
    } finally {
      setSubiendoNotaDoc(false);
      e.target.value = '';
    }
  };

  const handleQuitarNotaDocCliente = async () => {
    try {
      await actualizarNotaDocCliente(pedido.id, null);
      setNotaDocCliente(null);
      toast.success('Documento eliminado');
    } catch (err: any) {
      toast.error('Error al eliminar el documento: ' + (err.message || err));
    }
  };

  const [guardandoFechas, setGuardandoFechas] = useState(false);

  const handleFechaChange = async (campo: 'createdAt' | 'fechaEntrega', valor: string) => {
    if (!valor) return;
    setGuardandoFechas(true);
    try {
      await actualizarFechasPedido(pedido.id, { [campo]: valor });
      setPedidosState(prev => prev.map(p => p.id === pedido.id ? { ...p, [campo]: valor } : p));
      toast.success('Fecha actualizada');
    } catch (err: any) {
      toast.error('Error al actualizar la fecha: ' + (err.message || err));
    } finally {
      setGuardandoFechas(false);
    }
  };

  const {
    showFinalizarModal,
    setShowFinalizarModal,
    finalizarFields,
    setFinalizarFields,
    finalizarLoading,
    isSaving,
    generandoPDFs,
    imagenError,
    handleImageChange,
    handleSave,
  } = useFinalizarPedido(pedido, {
    onSuccess: () => {
      setPedidosState(prev =>
        prev.map(p => p.id === pedido.id ? { ...p, CERRAR_PRUEBA: true } : p)
      );
    },
    onPDFsGenerados: (clienteUrl, bitacoraUrl) => {
      setPedidosState(prev =>
        prev.map(p => p.id === pedido.id
          ? { ...p, CLIENTE_LINK: clienteUrl, BITACORA_LINK: bitacoraUrl }
          : p
        )
      );
    },
  });

  const {
    descargandoCliente,
    descargandoBitacora,
    enviandoWhatsApp,
    enviandoEmail,
    borrandoInforme,
    descargarReporteCliente,
    descargarBitacora,
    enviarWhatsApp,
    enviarEmail,
    borrarYGenerarInformes,
  } = useDescargarReportes(
    pedido,
    () => {
      setPedidosState(prev =>
        prev.map(p => p.id === pedido.id
          ? { ...p, CLIENTE_LINK: undefined, BITACORA_LINK: undefined }
          : p
        )
      );
    },
    (clienteUrl, bitacoraUrl) => {
      setPedidosState(prev =>
        prev.map(p => p.id === pedido.id
          ? { ...p, CLIENTE_LINK: clienteUrl, BITACORA_LINK: bitacoraUrl }
          : p
        )
      );
      onExpirarModoAdmin?.();
    },
  );

  const {
    generandoVistaCliente,
    generandoVistaBitacora,
    vistaPreviaReporteCliente,
    vistaPreviaBitacora,
  } = useVistaPrevia(pedido);

  const handleSelectPrueba = (pruebaData: PruebaData) => {
    onAddPrueba(pedido.id, pruebaData.nombre);
  };

  const handleLlenarPrueba = (prueba: string, pruebaIndex: number) => {
    onLlenarPrueba(pedido, prueba, pruebaIndex);
  };

  const handleUpload = (rowId: string, pruebaNombre: string, nomId?: string) => {
    setShowDocumentoUploadModal(true);
    setDocumentoUploadInfo({ rowId, prueba: pruebaNombre, nomId, pedidoId: pedido.id, origenNumero: pedido.numeroPedido });
  };

  // Orden manual (drag and drop) de las pruebas: solo se puede reordenar mientras el pedido
  // está abierto. El orden persistido en noms.orden también controla el orden en los PDFs.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const pruebasVisibles = pedido.pruebasSolicitadas.filter(prueba => !pedido.CERRAR_PRUEBA || prueba.ROW_ID);
  const dragIds = pruebasVisibles.map((prueba, idx) => prueba.nomId || `idx-${idx}`);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = dragIds.indexOf(String(active.id));
    const newIndex = dragIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordenado = arrayMove(pruebasVisibles, oldIndex, newIndex);
    const noVisibles = pedido.pruebasSolicitadas.filter(p => !pruebasVisibles.includes(p));
    const nuevasPruebasSolicitadas = [...reordenado, ...noVisibles];

    setPedidosState(prev => prev.map(p => p.id === pedido.id ? { ...p, pruebasSolicitadas: nuevasPruebasSolicitadas } : p));

    const actualizaciones = reordenado
      .map((p, idx) => ({ nomId: p.nomId, orden: idx }))
      .filter((u): u is { nomId: string; orden: number } => !!u.nomId);
    actualizarOrdenPruebas(actualizaciones).catch(err => console.error('No se pudo guardar el nuevo orden:', err));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <h2>Detalle del Pedido {pedido.numeroPedido}</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Pedido</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {reaperturaOrigen && (
            <div className="flex items-center justify-between gap-4 rounded-md border border-orange-300 bg-orange-50 p-3">
              <p className="text-sm text-orange-800">
                Reapertura con trazabilidad de <strong>{reaperturaOrigen.origenNumeroAnterior || 'un pedido anterior'}</strong>
                {' '}— solicitada por {reaperturaOrigen.solicitadoPor} el{' '}
                {new Date(reaperturaOrigen.createdAt).toLocaleDateString('es-MX')}
              </p>
              {reaperturaOrigen.evidenciaUrl && (
                <Button variant="outline" size="sm" className="shrink-0" asChild>
                  <a href={reaperturaOrigen.evidenciaUrl} target="_blank" rel="noopener noreferrer">
                    <FileSearch className="h-4 w-4 mr-2" />
                    Ver evidencia
                  </a>
                </Button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-muted-foreground">No. de Pedido</label>
              <p>{pedido.numeroPedido}</p>
            </div>
            <div>
              <label className="text-muted-foreground">Tipo de Prueba</label>
              <div>
                <Badge variant={pedido.tipoPrueba === "Fisico" ? "default" : "outline"}>
                  {pedido.tipoPrueba}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground">Fecha de Ingreso</label>
              {pedido.CERRAR_PRUEBA ? (
                <p>{formatFechaLocal(pedido.createdAt)}{formatHoraLocal(pedido.createdAt)}</p>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    className="h-8 w-36"
                    value={pedido.createdAt ? pedido.createdAt.split('T')[0] : ''}
                    disabled={guardandoFechas}
                    onChange={(e) => handleFechaChange('createdAt', `${e.target.value}T${pedido.createdAt?.split('T')[1]?.slice(0, 5) || '00:00'}`)}
                  />
                  <Input
                    type="time"
                    className="h-8 w-24"
                    value={pedido.createdAt?.split('T')[1]?.slice(0, 5) || '00:00'}
                    disabled={guardandoFechas}
                    onChange={(e) => { if (e.target.value) handleFechaChange('createdAt', `${pedido.createdAt ? pedido.createdAt.split('T')[0] : ''}T${e.target.value}`) }}
                  />
                </div>
              )}
            </div>
            <div>
              <label className="text-muted-foreground">Fecha de Emisión</label>
              {pedido.CERRAR_PRUEBA ? (
                <p>{formatFechaLocal(pedido.fechaEntrega)}{formatHoraLocal(pedido.fechaEntrega)}</p>
              ) : (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    className="h-8 w-36"
                    value={pedido.fechaEntrega ? pedido.fechaEntrega.split('T')[0] : ''}
                    disabled={guardandoFechas}
                    onChange={(e) => handleFechaChange('fechaEntrega', `${e.target.value}T${pedido.fechaEntrega?.split('T')[1]?.slice(0, 5) || '00:00'}`)}
                  />
                  <Input
                    type="time"
                    className="h-8 w-24"
                    value={pedido.fechaEntrega?.split('T')[1]?.slice(0, 5) || '00:00'}
                    disabled={guardandoFechas}
                    onChange={(e) => { if (e.target.value) handleFechaChange('fechaEntrega', `${pedido.fechaEntrega ? pedido.fechaEntrega.split('T')[0] : ''}T${e.target.value}`) }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <label className="text-muted-foreground">Artículos</label>
              <p>{pedido.articulos.join(", ")}</p>
            </div>
            {pedido.pruebasSolicitadas.some(p => p.ROW_ID) && !pedido.CERRAR_PRUEBA && (
              <div className="flex items-center gap-2 pt-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full shadow-sm gap-1.5 disabled:opacity-100"
                  onClick={vistaPreviaReporteCliente}
                  disabled={generandoVistaCliente || generandoVistaBitacora}
                >
                  {generandoVistaCliente ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  {generandoVistaCliente ? 'Generando...' : 'Vista previa Cliente'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full shadow-sm gap-1.5 disabled:opacity-100"
                  onClick={vistaPreviaBitacora}
                  disabled={generandoVistaCliente || generandoVistaBitacora}
                >
                  {generandoVistaBitacora ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
                  {generandoVistaBitacora ? 'Generando...' : 'Vista previa Bitácora'}
                </Button>
              </div>
            )}
          </div>
          <div>
            <label className="text-muted-foreground">Cantidad</label>
            <p>{pedido.CANTIDAD !== undefined ? pedido.CANTIDAD : 'Sin cantidad'}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>PRUEBAS SOLICITADAS</CardTitle>
            <Badge variant="outline">
              {pedido.CERRAR_PRUEBA
                ? pedido.pruebasSolicitadas.filter(p => p.ROW_ID).length
                : pedido.pruebasSolicitadas.length}
            </Badge>
          </div>
          {puedeCapturar && !pedido.CLIENTE_LINK && !pedido.BITACORA_LINK && (
            <div className="flex items-center gap-2">
              <label
                title="Subir documento Word para el reporte cliente"
                className="flex items-center gap-1.5 cursor-pointer border rounded-lg px-2.5 py-1.5 text-xs hover:bg-muted/50 transition-colors"
              >
                {subiendoNotaDoc ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span className="text-muted-foreground">
                  {notaDocCliente ? 'Word cargado' : 'Subir Word'}
                </span>
                <input
                  type="file"
                  accept=".docx"
                  className="hidden"
                  disabled={subiendoNotaDoc}
                  onChange={handleNotaDocClienteChange}
                />
              </label>
              {notaDocCliente && (
                <button
                  type="button"
                  onClick={handleQuitarNotaDocCliente}
                  title="Quitar documento Word"
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="min-h-[200px] flex flex-col">
            {pedido.pruebasSolicitadas.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                No items
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={dragIds} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-2">
                    {pruebasVisibles.map((prueba, idx) => (
                      <PedidoPruebaItem
                        key={prueba.nomId || `${prueba.nombre}__${prueba.ROW_ID || 'blank'}__${idx}`}
                        dragId={dragIds[idx]}
                        dragDisabled={!!pedido.CERRAR_PRUEBA}
                        prueba={prueba}
                        pruebaIndex={idx}
                        onLlenar={handleLlenarPrueba}
                        onRevisar={(pruebaNombre, rowId) => handleRevisarPrueba(pedido, pruebaNombre, rowId)}
                        onEditar={(pruebaNombre, rowId) => handleEditarPrueba?.(pedido, pruebaNombre, rowId)}
                        onContinuar={(pruebaNombre, rowId) => handleContinuarBorrador?.(pedido, pruebaNombre, rowId)}
                        onToggleReporteCliente={onToggleReporteCliente}
                        puedeCapturar={puedeCapturar}
                        onUpload={handleUpload}
                        modoEdicionAdmin={modoEdicionAdmin}
                        pedidoCerrado={!!pedido.CERRAR_PRUEBA}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}

            <>
              {/* Botón AGREGAR */}
              {!pedido.CERRAR_PRUEBA && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setIsSelectorOpen(true)} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    AGREGAR
                  </Button>
                </div>
              )}

              {/* Botón FINALIZAR PRUEBA: visible si todas las pruebas con ROW_ID están aceptadas o rechazadas */}
              {(() => {
                const conId = pedido.pruebasSolicitadas.filter(pr => pr.ROW_ID);
                const todasResueltas = conId.length > 0 && conId.every(pr => {
                  const estado = (pr.ACEPTADO || '').toLowerCase();
                  return estado === 'aceptado' || estado === 'rechazado';
                });
                return todasResueltas && !pedido.CERRAR_PRUEBA && puedeCerrarPedido;
              })() && (
                  <div className="mt-4">
                    <Button
                      className="w-full font-semibold text-sm"
                      style={{ backgroundColor: '#15803d', color: '#ffffff' }}
                      onClick={() => setShowFinalizarModal(true)}
                    >
                      FINALIZAR CARGA DE PRUEBAS
                    </Button>
                  </div>
                )}

              {/* Modal para datos extra de cierre */}
              <FinalizarPedidoModal
                open={showFinalizarModal}
                onClose={() => setShowFinalizarModal(false)}
                finalizarFields={finalizarFields}
                setFinalizarFields={setFinalizarFields}
                finalizarLoading={finalizarLoading}
                isSaving={isSaving}
                imagenError={imagenError}
                handleImageChange={handleImageChange}
                handleSave={handleSave}
              />

              {pedido.CERRAR_PRUEBA && (
                <div className="mt-4 text-center text-muted-foreground font-semibold">
                  Este pedido ya fue cerrado. No se pueden agregar más pruebas.
                </div>
              )}
            </>
          </div>
        </CardContent>
      </Card>

      {pedido.CERRAR_PRUEBA && puedeReabrir && (
        <div className="mb-3">
          <Button
            variant="outline"
            className="w-full border-orange-500 text-orange-600 hover:bg-orange-50"
            onClick={() => setShowReabrirModal(true)}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reabrir Pedido
          </Button>
        </div>
      )}

      <ReabrirPedidoModal
        open={showReabrirModal}
        onClose={() => setShowReabrirModal(false)}
        pedido={pedido}
        userName={userName}
        onReabierto={() => {
          setShowReabrirModal(false);
          onRefreshBackend?.();
        }}
      />

      {pedido.CERRAR_PRUEBA && (
        <div className="space-y-3">
          {modoEdicionAdmin ? (
            <Button
              variant="outline"
              className="w-full border-red-500 text-red-600 hover:bg-red-50"
              onClick={borrarYGenerarInformes}
              disabled={borrandoInforme}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {borrandoInforme ? 'Generando informes...' : 'Borrar y generar informes'}
            </Button>
          ) : (
            <>
              {puedeVerBitacoras && (
              <Button
                variant="outline"
                className="w-full border-blue-700 text-blue-700 hover:bg-blue-50"
                onClick={descargarBitacora}
                disabled={descargandoBitacora || generandoPDFs}
              >
                {(descargandoBitacora || generandoPDFs) ? 'Generando...' : 'Descargar Bitácora'}
              </Button>
              )}
              <Button
                className="w-full !bg-blue-700 !text-white hover:!bg-blue-800"
                onClick={descargarReporteCliente}
                disabled={descargandoCliente || generandoPDFs}
              >
                {(descargandoCliente || generandoPDFs) ? 'Generando...' : 'Descargar Reporte Cliente'}
              </Button>
              {pedido.telefono && (
                <Button
                  className="w-full flex items-center gap-2"
                  style={{ backgroundColor: '#16a34a', color: '#ffffff' }}
                  onClick={enviarWhatsApp}
                  disabled={enviandoWhatsApp}
                >
                  <MessageCircle className="h-4 w-4" />
                  {enviandoWhatsApp ? 'Generando...' : `Enviar por WhatsApp (${pedido.telefono})`}
                </Button>
              )}
              {pedido.correo && (
                <Button
                  className="w-full flex items-center gap-2"
                  style={{ backgroundColor: '#6b7280', color: '#ffffff' }}
                  onClick={enviarEmail}
                  disabled={enviandoEmail}
                >
                  <Mail className="h-4 w-4" />
                  {enviandoEmail ? 'Generando...' : `Enviar por Email (${pedido.correo})`}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      <PruebaSelector
        isOpen={isSelectorOpen}
        onClose={() => setIsSelectorOpen(false)}
        onSelectPrueba={handleSelectPrueba}
      />

      {showDocumentoUploadModal && (
        <DocumentoUploadModal
          open={showDocumentoUploadModal}
          onClose={() => setShowDocumentoUploadModal(false)}
          rowId={documentoUploadInfo?.rowId || ''}
          nomId={documentoUploadInfo?.nomId}
          pruebaNombre={documentoUploadInfo?.prueba || ''}
          pedidoId={documentoUploadInfo?.pedidoId || ''}
          origenNumero={documentoUploadInfo?.origenNumero || ''}
          onUpload={(url: string) => {
            if (documentoUploadInfo?.rowId) {
              setPedidosState(prev => prev.map(p =>
                p.id === pedido.id
                  ? { ...p, pruebasSolicitadas: p.pruebasSolicitadas.map(pr =>
                      pr.ROW_ID === documentoUploadInfo.rowId && pr.nombre === documentoUploadInfo.prueba
                        ? { ...pr, DOC: url } : pr
                    )}
                  : p
              ));
            }
          }}
        />
      )}
    </div>
  );
}
